import React, { useState, useEffect, useCallback } from 'react';
import ListLoadErrorBanner from '../../components/ui/ListLoadErrorBanner';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../../components/ui/Layout';
import Table from '../../components/ui/Table';
import ActionDropdown from '../../components/ui/ActionDropdown';
import ActionButtonsRow from '../../components/ui/ActionButtonsRow';
import SearchFilterCard, { FilterConfig } from '../../components/ui/SearchFilterCard';
import BulkActionsBar from '../../components/ui/BulkActionsBar';
import {
  RiUserStarLine,
  RiEyeLine,
  RiEditLine,
  RiDeleteBinLine,
  RiPenNibLine,
} from 'react-icons/ri';
import authorizedSignerService, { AuthorizedSigner } from '../../services/authorizedSignerService';
import companyService from '../../services/companyService';
import { showAlert, showConfirm } from '../../utils/alert';
import { getApiErrorMessageAsync } from '../../utils/mapFieldErrors';
import { queryKeys } from '../../queryClient';
import { useDeleteAuthorizedSigner } from '../../hooks/mutations/useAuthorizedSignerMutations';
import { checkApiHealth } from '../../utils/apiWithLogging';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import Pagination from '../../components/ui/Pagination';
import { useUserPageSize } from '../../hooks/useUserPageSize';
import { PAGINATION_CONFIG } from '../../config/pagination';
import { isNeverUpdated, formatDateTimeFull, getLocalDateString } from '../../utils/dateUtils';
import { downloadExcelFile } from '../../utils/downloadExcel';
import { CanCreate, CanEdit, CanDelete } from '../../components/auth/PermissionGuard';
import { usePermission } from '../../hooks/usePermission';
import { useClampPage } from '../../hooks/useClampPage';
import { runBulkDelete, bulkDeleteFailureMessage } from '../../utils/bulkDelete';
import { useDropUnknownCompanyFilter } from '../../hooks/useDropUnknownCompanyFilter';
import { detailNavState } from '../../components/detail/detailNavigation';
import {
  AUTHORIZED_SIGNER_NEW_PATH,
  authorizedSignerDetailUrl,
  authorizedSignerEditUrl,
} from '../../utils/authorizedSignerLinks';

const AuthorizedSignerPage: React.FC = () => {
  const { getUserId, hasValidUser } = useCurrentUser();
  const { canCreate, canEdit, canDelete, canExport } = usePermission();
  // ดู/เพิ่ม/แก้ไขเป็นหน้าเต็ม (มติ PO 28 ต.ค. 2026 — เดิมเป็นโมดัล) · ส่ง URL รายการ (ตัวกรอง/หน้า) ไปเป็น from
  // ให้ปุ่ม "กลับ" ของหน้ารายละเอียดพากลับมาที่ตัวกรองเดิม
  const navigate = useNavigate();
  const location = useLocation();
  const openSignerPage = (path: string) => navigate(path, { state: detailNavState(location) });

  // S1114 URL-sync: read URL params for lazy init (filter+page bidirectional)
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPageParam = searchParams.get('page');
  const urlCompanyParam = searchParams.get('company') || '';
  const urlStatusParam = searchParams.get('status') || '';

  // State สำหรับ pagination และ search
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    if (!urlPageParam) return 1;
    const n = parseInt(urlPageParam, 10);
    if (!Number.isFinite(n) || n < 1) return 1;
    return Math.min(n, 9999);
  });
  const [pageSize, setPageSize] = useState<number>(PAGINATION_CONFIG.DEFAULT_PAGE_SIZE);
  useUserPageSize(pageSize, setPageSize);
  // S1114 URL-sync: hydrate filters from URL
  const [companyFilter, setCompanyFilter] = useState<string>(urlCompanyParam);
  const [statusFilter, setStatusFilter] = useState<string>(
    urlStatusParam === 'active' || urlStatusParam === 'inactive' ? urlStatusParam : ''
  );

  // S1114 URL-sync: write filter+page to URL (Variant A overwrite — multi-field with inline page reset)
  const syncFiltersToUrl = useCallback((company: string, status: string, page: number) => {
    const newParams = new URLSearchParams();
    if (company) newParams.set('company', company);
    if (status === 'active' || status === 'inactive') newParams.set('status', status);
    if (page > 1) newParams.set('page', String(page));
    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    syncFiltersToUrl(companyFilter, statusFilter, currentPage);
  }, [companyFilter, statusFilter, currentPage, syncFiltersToUrl]);
  
  // Mutation hooks
  const deleteSignerMutation = useDeleteAuthorizedSigner();

  // State สำหรับ bulk action
  const [selectedSigners, setSelectedSigners] = useState<number[]>([]);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  
  // State สำหรับ companies
  const [companies, setCompanies] = useState<any[]>([]);
  const [companiesTotal, setCompaniesTotal] = useState<number | undefined>(undefined);
  useDropUnknownCompanyFilter(companies, companiesTotal, companyFilter, () => setCompanyFilter(''));
  
  // React Query for data fetching
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [...queryKeys.authorizedSigners.list(), {
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      company_id: companyFilter ? Number(companyFilter) : undefined,
      is_active: statusFilter ? statusFilter === 'active' : undefined
    }],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        company_id: companyFilter ? Number(companyFilter) : undefined,
        is_active: statusFilter ? statusFilter === 'active' : undefined
      };
      return await authorizedSignerService.getAuthorizedSigners(params);
    },
  });

  const signers = data?.items || [];
  const totalItems = data?.total || 0;
  useClampPage(currentPage, data?.total, pageSize, setCurrentPage, isLoading);
  const totalPages = Math.ceil(totalItems / pageSize);

  // โหลดข้อมูลบริษัท
  const fetchCompanies = async () => {
    try {
      const response = await companyService.getCompanies({ limit: 1000 });
      setCompanies(response.items);
      setCompaniesTotal(response.total);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  // โหลดข้อมูลเมื่อเปิดหน้า
  useEffect(() => {
    fetchCompanies();
  }, []);

  // เพิ่ม — หน้ารายการกรองบริษัทอยู่ = เติมบริษัทนั้นให้ในหน้าเพิ่ม (?company=)
  const handleAddSigner = () =>
    openSignerPage(companyFilter ? `${AUTHORIZED_SIGNER_NEW_PATH}?company=${encodeURIComponent(companyFilter)}` : AUTHORIZED_SIGNER_NEW_PATH);

  // ฟังก์ชันสำหรับลบ
  const handleDeleteSigner = (signer: AuthorizedSigner) => {
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบผู้มีอำนาจลงนาม ${signer.full_name} ใช่หรือไม่?`,
      async () => {
        try {
          await deleteSignerMutation.mutateAsync(signer.id);
        } catch (error) {
          console.error('Error deleting signer:', error);
        }
      },
      { isDanger: true }
    );
  };

  // ฟังก์ชันสำหรับ Bulk Actions
  // KD3 S2515: เปลี่ยนตัวกรอง/หน้าแล้วต้องล้างการเลือก — ไม่งั้นแถบ "เลือกแล้ว N" ค้าง และ "ลบที่เลือก" ลบแถวที่ผู้ใช้มองไม่เห็นแล้ว
  useEffect(() => {
    setSelectedSigners([]);
  }, [currentPage, pageSize, searchTerm, companyFilter, statusFilter]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSigners(signers.map(s => s.id));
    } else {
      setSelectedSigners([]);
    }
  };

  const handleSelectSigner = (signerId: number, checked: boolean) => {
    if (checked) {
      setSelectedSigners(prev => [...prev, signerId]);
    } else {
      setSelectedSigners(prev => prev.filter(id => id !== signerId));
    }
  };

  const handleBulkDelete = () => {
    showConfirm(
      'ยืนยันการลบ',
      `คุณต้องการลบผู้มีอำนาจลงนาม ${selectedSigners.length} รายการ ใช่หรือไม่?`,
      async () => {
        setBulkLoading(true);
        try {
          // KD3 S2120 (BUG-S2120-003): เดิมวน await หยุดที่รายการแรกที่ล้ม ไม่บอกเหตุผล/ไม่ refetch
          const result = await runBulkDelete(selectedSigners, (id) => authorizedSignerService.deleteAuthorizedSigner(id));
          setSelectedSigners(result.failed.map((f) => f.id));
          refetch();
          if (result.failed.length === 0) {
            showAlert('สำเร็จ', `ลบข้อมูล ${result.succeeded.length} รายการเรียบร้อยแล้ว`, 'success');
          } else {
            showAlert(
              result.succeeded.length ? 'ลบได้บางรายการ' : 'ลบไม่สำเร็จ',
              bulkDeleteFailureMessage('ผู้มีอำนาจลงนาม', result),
              result.succeeded.length ? 'warning' : 'error'
            );
          }
        } catch (error) {
          console.error('Error bulk deleting:', error);
          showAlert('เกิดข้อผิดพลาด', 'ไม่สามารถลบข้อมูลบางรายการได้', 'error');
        } finally {
          setBulkLoading(false);
        }
      },
      { isDanger: true }
    );
  };

  // ฟังก์ชัน Refresh
  const handleRefresh = () => {
    refetch();
  };

  // ฟังก์ชันสำหรับ export ข้อมูล
  const handleExportExcel = async () => {
    try {
      // ส่งตัวกรองที่กำลังใช้อยู่ไปด้วย — ไฟล์ต้องตรงกับที่เห็นบนจอ
      const blob = await authorizedSignerService.exportToExcel({
        search: searchTerm || undefined,
        company_id: companyFilter ? Number(companyFilter) : undefined,
        is_active: statusFilter ? statusFilter === 'active' : undefined,
      });
      downloadExcelFile(blob, `authorized_signers_${getLocalDateString()}.xlsx`);
      showAlert('สำเร็จ', 'ส่งออกข้อมูลเรียบร้อยแล้ว', 'success');
    } catch (error) {
      console.error('Error exporting data:', error);
      showAlert('เกิดข้อผิดพลาด', await getApiErrorMessageAsync(error, 'ไม่สามารถส่งออกข้อมูลได้'), 'error');
    }
  };

  // ฟังก์ชันสำหรับจัดการ Filter Change
  const handleFilterChange = (key: string, value: any) => {
    if (key === 'company') {
      setCompanyFilter(value);
    } else if (key === 'status') {
      setStatusFilter(value);
    }
    setCurrentPage(1);
  };

  // ฟังก์ชันสำหรับ Clear All Filters
  const handleClearAll = () => {
    setSearchTerm('');
    setCompanyFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  // Filters configuration
  const filters: FilterConfig[] = [
    {
      key: 'company',
      label: 'บริษัท',
      type: 'searchable-select',
      options: companies.map(c => ({ value: c.id, label: c.name })),
      placeholder: 'เลือกบริษัท',
      tagColor: 'primary'
    },
    {
      key: 'status',
      label: 'สถานะ',
      type: 'select',
      options: [
        { value: 'active', label: 'ใช้งาน' },
        { value: 'inactive', label: 'ไม่ใช้งาน' }
      ],
      placeholder: 'ทั้งหมด',
      tagColor: 'success'
    }
  ];

  // คอลัมน์ของตาราง
  const columns = [
    {
      key: 'select',
      title: (
        <input
          type="checkbox"
          aria-label="เลือกทั้งหมด"
          checked={signers.length > 0 && selectedSigners.length === signers.length}
          ref={(input) => { if (input) { input.indeterminate = selectedSigners.length > 0 && selectedSigners.length < signers.length; } }}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
        />
      ),
      width: '40px',
      render: (_: any, record: AuthorizedSigner) => (
        <input
          type="checkbox"
          aria-label={`เลือกรายการ ${record.name || record.id}`}
          checked={selectedSigners.includes(record.id)}
          onChange={(e) => handleSelectSigner(record.id, e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
        />
      ),
    },
    {
      key: 'company',
      title: 'บริษัท',
      width: '200px',
      render: (_: any, record: AuthorizedSigner) => (
        <div className="truncate" title={record.company?.name || '-'}>
          {record.company?.name || '-'}
        </div>
      ),
    },
    {
      key: 'full_name',
      title: 'ชื่อ-นามสกุล',
      width: '180px',
      render: (value: string) => (
        <div className="truncate" title={value}>
          {value}
        </div>
      ),
    },
    {
      key: 'position',
      title: 'ตำแหน่ง',
      width: '150px',
      render: (value: string) => (
        <div className="truncate" title={value}>
          {value}
        </div>
      ),
    },
    {
      key: 'email',
      title: 'อีเมล',
      width: '180px',
      render: (value: string) => (
        value ? (
          <a 
            href={`mailto:${value}`} 
            className="text-xs text-primary-600 hover:text-primary-800 underline truncate block"
            title={value}
          >
            {value}
          </a>
        ) : '-'
      ),
    },
    {
      key: 'phone',
      title: 'เบอร์โทร',
      width: '110px',
      render: (value: string) => (
        <span className="font-mono text-sm">
          {value || '-'}
        </span>
      ),
    },
    {
      key: 'is_active',
      title: 'สถานะ',
      width: '80px',
      render: (value: boolean) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          value
            ? 'bg-success-100 text-success-800'
            : 'bg-danger-100 text-danger-800'
        }`}>
          {value ? 'ใช้งาน' : 'ไม่ใช้งาน'}
        </span>
      ),
    },
    {
      key: 'is_primary',
      title: 'ผู้ลงนามหลัก',
      width: '100px',
      render: (value: boolean, record: AuthorizedSigner) => (
        value ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-info-100 text-info-800">
            ผู้ลงนามหลัก
          </span>
        ) : (
          <span className="text-secondary-400 text-xs">-</span>
        )
      ),
    },
    {
      key: 'created_at',
      title: 'วันที่สร้าง',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'updated_at',
      title: 'อัพเดทล่าสุด',
      sortable: true,
      render: (value: string | null) => formatDateTimeFull(value)
    },
    {
      key: 'actions',
      title: 'การจัดการ',
      width: '100px',
      sticky: true,
      render: (_: any, record: AuthorizedSigner) => (
        <ActionDropdown
          actions={[
            {
              label: 'ดูรายละเอียด',
              icon: <RiEyeLine />,
              onClick: () => openSignerPage(authorizedSignerDetailUrl(record.id)),
            },
            canEdit('authorized_signers') && {
              label: 'แก้ไข',
              icon: <RiEditLine />,
              onClick: () => openSignerPage(authorizedSignerEditUrl(record.id)),
            },
            canDelete('authorized_signers') && {
              divider: true
            },
            canDelete('authorized_signers') && {
              label: 'ลบ',
              icon: <RiDeleteBinLine />,
              onClick: () => handleDeleteSigner(record),
              className: 'text-danger-600 hover:text-danger-700',
            },
          ].filter(Boolean)}
        />
      ),
    },
  ];

  // Render loading state
  if (isLoading && !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Page Header with Executive Navy Theme */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <RiPenNibLine className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-secondary-900 leading-tight">ผู้มีอำนาจลงนาม</h1>
            <p className="text-xs text-secondary-500 mt-0.5">จัดการข้อมูลผู้มีอำนาจลงนามในสัญญา</p>
          </div>
        </div>
        {totalItems > 0 && (
          <span className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 rounded-lg bg-secondary-100 text-secondary-600 text-xs font-medium self-start sm:self-auto">
            {totalItems.toLocaleString()} รายการ
          </span>
        )}
      </div>

        <ListLoadErrorBanner error={error} entityLabel="ผู้ลงนามอนุมัติ" onRetry={() => refetch()} />

        {/* Action Buttons */}
        <ActionButtonsRow
          onAdd={canCreate('authorized_signers') ? handleAddSigner : undefined}
          addLabel="เพิ่มผู้ลงนาม"
          onRefresh={handleRefresh}
          refreshLoading={isFetching}
          onExport={canExport('authorized_signers') ? handleExportExcel : undefined}
          exportDisabled={!signers?.length}
        />

        {/* Search and Filters */}
        <SearchFilterCard
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          searchPlaceholder="ค้นหาชื่อ, ตำแหน่ง, อีเมล, เบอร์โทร..."
          filters={filters}
          filterValues={{ company: companyFilter, status: statusFilter }}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAll}
        />

        {/* Bulk Actions Bar */}
        {canDelete('authorized_signers') && (
          <BulkActionsBar
            selectedCount={selectedSigners.length}
            onBulkDelete={handleBulkDelete}
            onClearSelection={() => setSelectedSigners([])}
            loading={bulkLoading}
          />
        )}

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
          <Table
            columns={canDelete('authorized_signers') ? columns : columns.filter(c => c.key !== 'select')}  /* KD3 S2122 (BUG-S2122-004): ไม่มีสิทธิ์ลบ = ไม่มีช่องติ๊ก (ติ๊กแล้วไม่มีอะไรให้ทำ) */
            data={signers}
            loading={isLoading}
            emptyMessage="ไม่พบข้อมูลผู้มีอำนาจลงนาม"
          />
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-secondary-200 px-6 py-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(newSize) => {
                  setPageSize(newSize);
                  setCurrentPage(1);
                }}
                pageSizeOptions={PAGINATION_CONFIG.PAGE_SIZE_OPTIONS}
              />
            </div>
          )}
        </div>
      </div>

    </Layout>
  );
};

export default AuthorizedSignerPage;